/**
 * A syntax check for the policy editor, strong enough to catch the mistakes that actually
 * get made in that box and conservative enough never to cry wolf.
 *
 * It is NOT a Rego parser. OPA's parser is the authority (the templates are parsed by real
 * OPA in CI, and the evaluator is what finally judges a stored policy) - but OPA is a Go
 * binary, so the editor cannot ask it. What the editor can do is lex the module and check
 * the things a lexer sees with certainty: unterminated strings, unbalanced delimiters, a
 * missing `package` header, characters Rego has no use for, and the one grammar rule authors
 * trip over constantly - Rego v1 requires `if` before a rule body, so every `deny[msg] { … }`
 * copied off the internet is a parse error here.
 *
 * The bias is deliberate: a false negative costs an author nothing (OPA still rejects the
 * policy on save), while a false positive paints a red border on correct Rego and blocks the
 * save. So every check below either holds for all Rego v1 or is not made at all; where the
 * token stream is ambiguous the checker stays quiet.
 */

export interface RegoSyntaxError {
  /** 1-based, as Monaco and humans both count. */
  line: number
  column: number
  /** Exclusive end column, for the editor's squiggle. */
  endColumn: number
  message: string
}

// ── Lexer ─────────────────────────────────────────────────────────────────────

type TokenKind = 'ident' | 'keyword' | 'number' | 'string' | 'punct'

export interface RegoToken {
  kind: TokenKind
  text: string
  line: number
  column: number
  endColumn: number
}

/** Rego v1 keywords plus the scalar literals - none of them can head a rule, which is what
 *  the body-brace test below cares about. */
const KEYWORDS = new Set([
  'package', 'import', 'default', 'not', 'with', 'as', 'else', 'some', 'every', 'in',
  'contains', 'if', 'true', 'false', 'null',
])

const TWO_CHAR = new Set([':=', '==', '!=', '>=', '<='])
const SINGLE   = new Set([...'{}[]().,;:+-*/%<>=|&'])

/** Characters that are never Rego, and what the author probably meant by them. */
const STRAY: Record<string, string> = {
  "'": 'Rego has no single-quoted strings - use double quotes.',
  '!': '`!` is only valid as part of `!=` - negate with the `not` keyword.',
  '?': '`?` is not a Rego operator.',
  '$': '`$` is not valid in Rego - a variable is a bare name.',
  '@': '`@` is not valid in Rego.',
  '\\': 'A stray backslash - escapes only mean something inside a string.',
}

const isIdentStart = (ch: string) => /[A-Za-z_]/.test(ch)
const isIdentPart  = (ch: string) => /[A-Za-z0-9_]/.test(ch)
const isDigit      = (ch: string) => ch >= '0' && ch <= '9'

/**
 * Lex a module into tokens, reporting the errors a lexer can be sure of. Comments are
 * dropped; strings are kept whole (their contents can say anything, so nothing downstream
 * looks inside them).
 */
export function tokenizeRego(src: string): { tokens: RegoToken[]; errors: RegoSyntaxError[] } {
  const tokens: RegoToken[] = []
  const errors: RegoSyntaxError[] = []
  let i = 0, line = 1, col = 1

  const push = (kind: TokenKind, text: string, l: number, c: number) => {
    tokens.push({ kind, text, line: l, column: c, endColumn: c + text.length })
  }

  while (i < src.length) {
    const ch = src[i]

    if (ch === '\n') { i++; line++; col = 1; continue }
    if (ch === ' ' || ch === '\t' || ch === '\r') { i++; col++; continue }
    if (ch === '#') { while (i < src.length && src[i] !== '\n') i++; continue }

    // Quoted string. Rego's cannot span lines, so an unclosed one ends at the newline -
    // which is exactly the author's typo, not a 200-line string.
    if (ch === '"') {
      const startLine = line, startCol = col
      let j = i + 1, closed = false
      while (j < src.length) {
        const c2 = src[j]
        if (c2 === '\n') break
        if (c2 === '\\') {
          if (src[j + 1] === '\n' || j + 1 >= src.length) break
          j += 2
          continue
        }
        j++
        if (c2 === '"') { closed = true; break }
      }
      const text = src.slice(i, j)
      if (!closed) {
        errors.push({
          line: startLine, column: startCol, endColumn: startCol + Math.max(text.length, 1),
          message: 'Unterminated string - this `"` is never closed.',
        })
      }
      push('string', text, startLine, startCol)
      col += text.length
      i = j
      continue
    }

    // Raw string: backticks, and these DO span lines, so the position has to be tracked
    // through the token rather than added to the column at the end.
    if (ch === '`') {
      const startLine = line, startCol = col
      let j = i + 1, l = line, c = col + 1, closed = false
      while (j < src.length) {
        if (src[j] === '`') { j++; c++; closed = true; break }
        if (src[j] === '\n') { l++; c = 1 } else { c++ }
        j++
      }
      if (!closed) {
        errors.push({
          line: startLine, column: startCol, endColumn: startCol + 1,
          message: 'Unterminated raw string - this backtick is never closed.',
        })
      }
      push('string', src.slice(i, j), startLine, startCol)
      i = j; line = l; col = c
      continue
    }

    if (isDigit(ch)) {
      let j = i + 1
      while (j < src.length && isDigit(src[j])) j++
      if (src[j] === '.' && isDigit(src[j + 1])) {
        j += 2
        while (j < src.length && isDigit(src[j])) j++
      }
      if (src[j] === 'e' || src[j] === 'E') {
        let k = j + 1
        if (src[k] === '+' || src[k] === '-') k++
        if (isDigit(src[k])) {
          j = k
          while (j < src.length && isDigit(src[j])) j++
        }
      }
      const text = src.slice(i, j)
      push('number', text, line, col)
      i = j; col += text.length
      continue
    }

    if (isIdentStart(ch)) {
      let j = i + 1
      while (j < src.length && isIdentPart(src[j])) j++
      const text = src.slice(i, j)
      push(KEYWORDS.has(text) ? 'keyword' : 'ident', text, line, col)
      i = j; col += text.length
      continue
    }

    const pair = src.slice(i, i + 2)
    if (TWO_CHAR.has(pair)) {
      push('punct', pair, line, col)
      i += 2; col += 2
      continue
    }

    if (SINGLE.has(ch)) {
      push('punct', ch, line, col)
      i++; col++
      continue
    }

    errors.push({
      line, column: col, endColumn: col + 1,
      message: STRAY[ch] ?? `\`${ch}\` is not valid in Rego.`,
    })
    i++; col++
  }

  return { tokens, errors }
}

// ── Structural checks ─────────────────────────────────────────────────────────

const OPENER_FOR: Record<string, string> = { '}': '{', ']': '[', ')': '(' }
const CLOSER_FOR: Record<string, string> = { '{': '}', '[': ']', '(': ')' }

/**
 * Whether a top-level `{` opens a rule BODY rather than a set/object value.
 *
 * At depth zero the only things that can sit immediately before a body brace are the rule
 * head's last token (a name, or the `]` / `)` ending a key or argument list) or the `if` /
 * `else` that introduces the body. Everything else - `:=`, `=`, `in`, `contains`, a comma,
 * an operator - means the brace is a composite value, and those are never checked.
 */
function opensRuleBody(prev: RegoToken | undefined): boolean {
  if (!prev) return false
  return prev.kind === 'ident' || prev.text === ']' || prev.text === ')'
    || prev.text === 'if' || prev.text === 'else'
}

/**
 * Lex-level and structural validation of a Rego module. Empty input is not an error - the
 * editor starts empty, and the form's own required check covers that.
 *
 * Structural checking stops at the first finding: once the delimiter stack is wrong every
 * subsequent brace is suspect, and five cascading errors from one missing `}` tell the author
 * less than the one that caused them.
 */
export function validateRego(src: string): RegoSyntaxError[] {
  if (!src.trim()) return []

  const { tokens, errors } = tokenizeRego(src)

  // A raw string with no closing backtick eats the rest of the module, so every delimiter
  // after it is unbalanced for a reason that is not the author's next problem. Report it alone.
  const swallowed = errors.find((e) => e.message.startsWith('Unterminated raw string'))
  if (swallowed) return [swallowed]

  // Every Terramantle policy is a module, and a module opens with its package.
  if (tokens.length > 0) {
    const first = tokens[0]
    if (first.text !== 'package') {
      errors.push({
        line: first.line, column: first.column, endColumn: first.endColumn,
        message: 'A policy must open with its package declaration - `package terramantle.policy`.',
      })
    } else if (tokens[1]?.kind !== 'ident' || tokens[1].line !== first.line) {
      errors.push({
        line: first.line, column: first.column, endColumn: first.endColumn,
        message: '`package` needs a name - `package terramantle.policy`.',
      })
    }
    for (const dupe of tokens.slice(1).filter((t) => t.text === 'package')) {
      errors.push({
        line: dupe.line, column: dupe.column, endColumn: dupe.endColumn,
        message: 'A module takes one `package` declaration, and it comes first.',
      })
    }
  }

  const open: RegoToken[] = []
  /** Tokens of the statement being read, while it is still at depth zero - i.e. the rule head. */
  let head: RegoToken[] = []
  let stopped = false

  for (let k = 0; k < tokens.length; k++) {
    const t = tokens[k]
    if (open.length === 0 && t.text !== '{') head.push(t)

    if (t.text === '{' || t.text === '[' || t.text === '(') {
      if (t.text === '{' && open.length === 0 && opensRuleBody(tokens[k - 1])) {
        const intro = tokens[k - 1]!.text
        if (intro !== 'if' && intro !== 'else') {
          // The v0 forms: `allow { … }`, `deny[msg] { … }`, `f(x) { … }`. All three parse
          // under OPA 0.x and none of them under the 1.x the scanners run.
          errors.push({
            line: t.line, column: t.column, endColumn: t.endColumn,
            message: head.some((h) => h.text === '[')
              ? 'Rego v1 needs `contains` and `if` on a multi-value rule - `deny contains msg if { … }`.'
              : 'Rego v1 requires `if` before a rule body - `deny contains msg if { … }`.',
          })
          stopped = true
          break
        }
      }
      open.push(t)
      continue
    }

    if (t.text === '}' || t.text === ']' || t.text === ')') {
      const want = OPENER_FOR[t.text]
      const top = open[open.length - 1]
      if (!top) {
        errors.push({
          line: t.line, column: t.column, endColumn: t.endColumn,
          message: `Unexpected \`${t.text}\` - there is no \`${want}\` open to close.`,
        })
        stopped = true
        break
      }
      if (top.text !== want) {
        errors.push({
          line: t.line, column: t.column, endColumn: t.endColumn,
          message: `Expected \`${CLOSER_FOR[top.text]}\` to close the \`${top.text}\` on line ${top.line}, found \`${t.text}\`.`,
        })
        stopped = true
        break
      }
      open.pop()
      if (t.text === '}' && open.length === 0) head = []
      continue
    }
  }

  // Whatever is still open at the end: the innermost one is the closer actually missing.
  // Skipped once the pass gave up, since the stack from there on is guesswork.
  const unclosed = stopped ? undefined : open[open.length - 1]
  if (unclosed) {
    errors.push({
      line: unclosed.line, column: unclosed.column, endColumn: unclosed.endColumn,
      message: `Unclosed \`${unclosed.text}\` - nothing closes it with \`${CLOSER_FOR[unclosed.text]}\`.`,
    })
  }

  errors.sort((a, b) => a.line - b.line || a.column - b.column)
  // One mistake, one row: a `'quoted'` string trips the same message at both quotes, and two
  // identical lines in the bar read as a bug in the bar rather than two problems in the Rego.
  const seen = new Set<string>()
  return errors
    .filter((e) => {
      const key = `${e.line}:${e.message}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .slice(0, 6)
}

// ── One semantic check, kept well away from the syntax ones ───────────────────

/**
 * Whether the buffer mentions `deny` as CODE at all - comments and strings do not count, because
 * the tokenizer drops the first and keeps the second whole.
 *
 * This is not a syntax check and is deliberately not part of `validateRego`: a module with no
 * `deny` rule parses perfectly, OPA is happy with it, and it is only wrong in Terramantle's terms,
 * where the evaluator queries `data.terramantle.policy.deny` and a policy that defines nothing
 * there can never fail anything. It exists because the new-policy editor now opens on a scaffold
 * whose guidance is entirely commented out: without it, "type a name and press Create" saves a
 * policy that silently passes everything, which is the worst possible outcome for a gate.
 *
 * It asks the weakest question that still catches that case. Not "is there a well-formed deny
 * rule" - that would have to judge Rego it might get wrong, and a false positive here blocks a
 * save, which is the one thing the checker in this file refuses to risk.
 */
export function mentionsDeny(src: string): boolean {
  return tokenizeRego(src).tokens.some((t) => t.kind === 'ident' && t.text === 'deny')
}
